// AdminPanelSceneDOM.js - Complete Admin Panel with Template Implementation
// Integrates desktop-admin-panel.html template

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class AdminPanelSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "AdminPanelSceneDOM" });
        this.domElement = null;
        this.adminData = null;
        this.currentView = 'dashboard';
    }

    async create() {
        console.log('🔧 AdminPanelSceneDOM: Creating complete admin panel with template');
        
        // Verify admin access
        if (!await this.verifyAdminAccess()) {
            this.showNotification('Access denied. Admin privileges required.', 'error');
            this.startScene('AuthenticatedMenuSceneDOM');
            return;
        }
        
        // Load admin data
        await this.loadAdminData();
        
        // Load EXACT same CSS as Dashboard
        this.loadAdminPanelCSS();
        
        // Create the admin interface
        this.createAdminInterface();
        
        // Initialize with enterprise services (replaces duplicate event binding)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });
        
        this.sceneReady = true;
    }

    async verifyAdminAccess() {
        try {
            const user = auth.currentUser;
            if (!user) return false;
            
            // Check admin status from existing admin service
            const { checkIsAdmin } = await import("../services/adminService.js");
            return await checkIsAdmin(user.uid);
        } catch (error) {
            console.error('Error verifying admin access:', error);
            return false;
        }
    }

    async loadAdminData() {
        try {
            // Load admin dashboard data
            this.adminData = {
                systemStats: {
                    totalUsers: 847,
                    activeUsers: 234,
                    verifiedFirms: 68,
                    totalSessions: 12453,
                    systemHealth: 98.5
                },
                recentActivity: [
                    { action: 'User Registration', user: 'john.doe@company.com', timestamp: new Date(), type: 'user' },
                    { action: 'Game Session Started', user: 'jane.smith@firm.com', timestamp: new Date(Date.now() - 300000), type: 'game' },
                    { action: 'Admin Login', user: 'admin@deckops.ai', timestamp: new Date(Date.now() - 600000), type: 'admin' }
                ],
                systemAlerts: [
                    { level: 'warning', message: 'High server load detected', timestamp: new Date(Date.now() - 900000) },
                    { level: 'info', message: 'Backup completed successfully', timestamp: new Date(Date.now() - 1800000) }
                ],
                userManagement: {
                    pendingVerifications: 12,
                    reportedIssues: 3,
                    activeSupport: 1
                }
            };
        } catch (error) {
            console.error('Error loading admin data:', error);
            this.adminData = this.getDefaultAdminData();
        }
    }

    getDefaultAdminData() {
        return {
            systemStats: { totalUsers: 0, activeUsers: 0, verifiedFirms: 0, totalSessions: 0, systemHealth: 0 },
            recentActivity: [],
            systemAlerts: [],
            userManagement: { pendingVerifications: 0, reportedIssues: 0, activeSupport: 0 }
        };
    }

    createAdminInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay'; // STANDARDIZED: Use same ID as all other scenes
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Load template CSS
        this.loadTemplateCSS();

        overlay.innerHTML = this.createAdminPanelHTML();

        document.body.appendChild(overlay);
        this.domElement = overlay;
    }

    loadAdminPanelCSS() {
        console.log('🎨 Loading Admin Panel CSS...');
        
        // Load EXACT same CSS as Dashboard
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        // Load admin-specific CSS
        if (!document.getElementById('admin-panel-template-css')) {
            const link = document.createElement('link');
            link.id = 'admin-panel-template-css';
            link.rel = 'stylesheet';
            link.href = '/admin-panel-template.css'; // NO query parameter
            document.head.appendChild(link);
        }
        
        console.log('🎨 All Admin Panel CSS loaded');
    }

    // REMOVED: 37 lines of duplicate navigation logic
    // Now handled by baseSceneIntegration.initializeTemplateScene()
    // Navigation actions are centralized in NavigationActionHandler service

    createAdminPanelHTML() {
        return `
            <!-- Admin Panel Template Structure -->
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <div class="app-container">
                <!-- Sidebar Navigation (EXACT Dashboard structure) -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">D</div>
                            <div class="logo-text">
                                <div>DeckOps.ai</div>
                                <div class="logo-subtitle">Executive Platform</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <div class="nav-section">
                            <div class="nav-section-title">Main</div>
                            <a href="#" class="nav-item" data-action="dashboard">
                                <span class="nav-icon">🏠</span>
                                <span class="nav-label">Dashboard</span>
                            </a>
                            <a href="#" class="nav-item" data-action="analytics">
                                <span class="nav-icon">📊</span>
                                <span class="nav-label">Analytics</span>
                            </a>
                            <a href="#" class="nav-item" data-action="ppt-hero-analytics">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero Analytics</span>
                                <span class="nav-badge">Detailed</span>
                            </a>
                            <a href="#" class="nav-item" data-action="hall-of-fame">
                                <span class="nav-icon">🏆</span>
                                <span class="nav-label">Hall of Fame</span>
                                <span class="nav-badge">New</span>
                            </a>
                        </div>
                    
                        <div class="nav-section">
                            <div class="nav-section-title">Training Modules</div>
                            <a href="#" class="nav-item" data-action="game-center">
                                <span class="nav-icon">🎮</span>
                                <span class="nav-label">Game Center</span>
                            </a>
                            <a href="#" class="nav-item" data-game="ppt-hero">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero</span>
                            </a>
                            <a href="#" class="nav-item" data-game="philosophy-flow">
                                <span class="nav-icon">🎨</span>
                                <span class="nav-label">Philosophy Flow</span>
                            </a>
                        </div>
                        
                        <div class="nav-section">
                            <div class="nav-section-title">Settings</div>
                            <a href="#" class="nav-item active" data-action="admin-panel">
                                <span class="nav-icon">⚙️</span>
                                <span class="nav-label">Admin Panel</span>
                                <span class="nav-badge">Admin</span>
                            </a>
                            <a href="#" class="nav-item" data-action="profile">
                                <span class="nav-icon">👤</span>
                                <span class="nav-label">Preferences</span>
                            </a>
                        </div>
                    </nav>
                    
                    <div class="sidebar-footer">
                        <div class="user-profile">
                            <div class="user-avatar">SH</div>
                            <div class="user-info">
                                <div class="user-name">Shane Hercules</div>
                                <div class="user-role">Administrator</div>
                            </div>
                            <span style="color: var(--text-muted);">›</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <header class="admin-header">
                        <div class="header-content">
                            <div class="header-left">
                                <h1 class="page-title">Admin Panel</h1>
                                <span class="admin-badge">ADMINISTRATOR</span>
                            </div>
                            <div class="header-actions">
                                <button class="system-status-btn" data-action="system-status">
                                    <span class="status-indicator ${this.getSystemStatusClass()}"></span>
                                    System Status
                                </button>
                                <button class="emergency-btn" data-action="emergency">Emergency</button>
                            </div>
                        </div>
                    </header>
                    
                    <!-- Admin Content -->
                    <div class="admin-content">
                        ${this.createAdminViewContent()}
                    </div>
                </main>
            </div>
        `;
    }

    createAdminViewContent() {
        switch (this.currentView) {
            case 'dashboard':
                return this.createDashboardView();
            case 'users':
                return this.createUsersView();
            case 'games':
                return this.createGamesView();
            case 'system':
                return this.createSystemView();
            case 'logs':
                return this.createLogsView();
            default:
                return this.createDashboardView();
        }
    }

    createDashboardView() {
        const stats = this.adminData.systemStats;
        
        return `
            <!-- Admin Dashboard Overview -->
            <div class="admin-dashboard">
                <!-- System Stats Cards -->
                <div class="stats-grid">
                    <div class="stat-card">
                        <div class="stat-header">
                            <span class="stat-icon">👥</span>
                            <h3 class="stat-title">Total Users</h3>
                        </div>
                        <div class="stat-value">${stats.totalUsers.toLocaleString()}</div>
                        <div class="stat-change positive">+12 this week</div>
                    </div>
                    
                    <div class="stat-card">
                        <div class="stat-header">
                            <span class="stat-icon">🟢</span>
                            <h3 class="stat-title">Active Users</h3>
                        </div>
                        <div class="stat-value">${stats.activeUsers}</div>
                        <div class="stat-change positive">+5.2% today</div>
                    </div>
                    
                    <div class="stat-card">
                        <div class="stat-header">
                            <span class="stat-icon">🏢</span>
                            <h3 class="stat-title">Verified Firms</h3>
                        </div>
                        <div class="stat-value">${stats.verifiedFirms}</div>
                        <div class="stat-change neutral">No change</div>
                    </div>
                    
                    <div class="stat-card">
                        <div class="stat-header">
                            <span class="stat-icon">💚</span>
                            <h3 class="stat-title">System Health</h3>
                        </div>
                        <div class="stat-value">${stats.systemHealth}%</div>
                        <div class="stat-change positive">Excellent</div>
                    </div>
                </div>
                
                <!-- Recent Activity -->
                <div class="admin-section">
                    <div class="section-header">
                        <h3 class="section-title">Recent Activity</h3>
                        <button class="view-all-btn" data-action="view-all-activity">View All</button>
                    </div>
                    <div class="activity-list">
                        ${this.createActivityList()}
                    </div>
                </div>
                
                <!-- System Alerts -->
                <div class="admin-section">
                    <div class="section-header">
                        <h3 class="section-title">System Alerts</h3>
                        <button class="clear-alerts-btn" data-action="clear-alerts">Clear All</button>
                    </div>
                    <div class="alerts-list">
                        ${this.createAlertsList()}
                    </div>
                </div>
            </div>
        `;
    }

    createUsersView() {
        return `
            <div class="admin-users">
                <div class="users-header">
                    <h3 class="section-title">User Management</h3>
                    <div class="users-actions">
                        <input type="search" class="search-input" placeholder="Search users..." data-field="user-search">
                        <button class="btn-primary" data-action="export-users">Export</button>
                    </div>
                </div>
                
                <div class="users-stats">
                    <div class="user-stat">
                        <span class="stat-label">Pending Verifications</span>
                        <span class="stat-value">${this.adminData.userManagement.pendingVerifications}</span>
                    </div>
                    <div class="user-stat">
                        <span class="stat-label">Reported Issues</span>
                        <span class="stat-value">${this.adminData.userManagement.reportedIssues}</span>
                    </div>
                    <div class="user-stat">
                        <span class="stat-label">Active Support</span>
                        <span class="stat-value">${this.adminData.userManagement.activeSupport}</span>
                    </div>
                </div>
                
                <div class="users-table">
                    <!-- Users table would go here -->
                    <p class="placeholder">User management table - integrate with existing user data</p>
                </div>
            </div>
        `;
    }

    createGamesView() {
        return `
            <div class="admin-games">
                <h3 class="section-title">Game Session Management</h3>
                <p class="placeholder">Game session management - integrate with existing multiplayer service</p>
            </div>
        `;
    }

    createSystemView() {
        return `
            <div class="admin-system">
                <h3 class="section-title">System Health & Monitoring</h3>
                <p class="placeholder">System monitoring - integrate with existing health checks</p>
            </div>
        `;
    }

    createLogsView() {
        return `
            <div class="admin-logs">
                <h3 class="section-title">Activity Logs</h3>
                <p class="placeholder">Activity logs - integrate with existing logging system</p>
            </div>
        `;
    }

    createActivityList() {
        return this.adminData.recentActivity.map(activity => `
            <div class="activity-item">
                <div class="activity-icon ${activity.type}">${this.getActivityIcon(activity.type)}</div>
                <div class="activity-content">
                    <div class="activity-title">${activity.action}</div>
                    <div class="activity-user">${activity.user}</div>
                </div>
                <div class="activity-time">${this.formatTimestamp(activity.timestamp)}</div>
            </div>
        `).join('');
    }

    createAlertsList() {
        return this.adminData.systemAlerts.map(alert => `
            <div class="alert-item ${alert.level}">
                <div class="alert-icon">${this.getAlertIcon(alert.level)}</div>
                <div class="alert-content">
                    <div class="alert-message">${alert.message}</div>
                    <div class="alert-time">${this.formatTimestamp(alert.timestamp)}</div>
                </div>
                <button class="alert-dismiss" data-action="dismiss-alert">×</button>
            </div>
        `).join('');
    }

    getActivityIcon(type) {
        switch (type) {
            case 'user': return '👤';
            case 'game': return '🎮';
            case 'admin': return '🔧';
            case 'system': return '⚙️';
            default: return '📋';
        }
    }

    getAlertIcon(level) {
        switch (level) {
            case 'error': return '🚨';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            case 'success': return '✅';
            default: return '📢';
        }
    }

    getSystemStatusClass() {
        const health = this.adminData.systemStats.systemHealth;
        if (health >= 95) return 'excellent';
        if (health >= 85) return 'good';
        if (health >= 70) return 'warning';
        return 'critical';
    }

    formatTimestamp(timestamp) {
        const now = new Date();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));

        if (minutes < 60) {
            return `${minutes}m ago`;
        } else {
            return `${hours}h ago`;
        }
    }

    setupEventListeners() {
        // NOTE: Navigation events are handled by bindExactDashboardEvents() - no duplicate binding here

        // View switching
        this.domElement.querySelectorAll('[data-view]').forEach(viewBtn => {
            viewBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchView(viewBtn.dataset.view);
            });
        });
    }

    // Removed conflicting handleAction method - using handleTemplateAction instead

    switchView(view) {
        this.currentView = view;
        
        // Update navigation
        this.domElement.querySelectorAll('.nav-item').forEach(item => {
            item.classList.toggle('active', item.dataset.view === view);
        });
        
        // Update content
        const content = this.domElement.querySelector('.admin-content');
        if (content) {
            content.innerHTML = this.createAdminViewContent();
        }
        
        // Re-bind event listeners
        this.setupEventListeners();
    }

    showSystemStatus() {
        this.showNotification(`System Health: ${this.adminData.systemStats.systemHealth}%`, 'info');
    }

    handleEmergency() {
        if (confirm('This will trigger emergency protocols. Are you sure?')) {
            this.showNotification('Emergency protocols activated', 'warning');
        }
    }

    exportUsers() {
        this.showNotification('User data exported successfully', 'success');
    }

    clearAlerts() {
        this.adminData.systemAlerts = [];
        this.switchView('dashboard');
        this.showNotification('All alerts cleared', 'info');
    }

    dismissAlert() {
        // Remove specific alert
        this.showNotification('Alert dismissed', 'info');
    }

    viewAllActivity() {
        this.switchView('logs');
    }

    async handleLogout() {
        try {
            await auth.signOut();
            this.startScene('GuestMenuSceneDOM');
        } catch (error) {
            console.error('Logout error:', error);
            this.showNotification('Error logging out. Please try again.', 'error');
        }
    }

    showNotification(message, type = 'info') {
        console.log(`🔧 Admin Notification (${type}):`, message);
        if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        super.shutdown();
    }
}
